import { canvasFlatten, canvasThreshold, createCanvas } from './canvas.utils'

/**
 * StarGraphic Language Commands
 */
class StarGraphicEncoder {
  private buffer: (number | Uint8Array)[] = []

  constructor() {
    this.reset()
  }

  /**
   * Reset the state of the object
   */
  private reset(): void {
    this.buffer = []
  }

  /**
   * Add commands to the buffer
   * @param {Array} value Array of numbers to add to the buffer
   */
  private queue(value: (number | Uint8Array)[]): void {
    value.forEach(item => this.buffer.push(item))
  }

  /**
   * Initialize the printer
   * @return {object} Return the object, for easy chaining commands
   */
  initialize(): this {
    // ESC RS a n ESC * r A ESC * r P n NUL
    this.queue([0x1b, 0x1e, 0x61, 0x00]) // ESC RS a 0
    this.queue([0x1b, 0x2a, 0x72, 0x41]) // ESC * r A
    this.queue([0x1b, 0x2a, 0x72, 0x50, 0x30, 0x00]) // ESC * r P 0 NUL
    return this
  }

  /**
   * Change the alignment
   * @param {string} value Alignment value ('left', 'center', 'right')
   * @return {object} Return the object, for easy chaining commands
   */
  align(_value: 'left' | 'center' | 'right'): this {
    // Not supported
    return this
  }

  /**
   * Print newline
   * @param {number} count Number of newlines
   * @return {object} Return the object, for easy chaining commands
   */
  newline(count: number = 1): this {
    for (let i = 0; i < count; i++) {
      // ESC * r Y n NUL - feed new line
      const charWidth = 16 // Valor típico para StarGraphic
      this.queue([0x1b, 0x2a, 0x72, 0x59, charWidth * 2, 0x00])
    }
    return this
  }

  /**
   * Encode an image
   * @param {ImageData} image ImageData object
   * @param {number} width Width of the image
   * @param {number} height Height of the image
   * @return {object} Return the object, for easy chaining commands
   */
  image(element: CanvasImageSource, width: number, height: number): this {
    if (width % 8 !== 0) {
      throw new Error('Width must be a multiple of 8')
    }
    if (height % 8 !== 0) {
      throw new Error('Height must be a multiple of 8')
    }

    const canvas = createCanvas(width, height)
    const context = canvas.getContext('2d')
    if (!context) throw new Error('Could not get 2d context from canvas')

    context.drawImage(element, 0, 0, width, height)
    let image = context.getImageData(0, 0, width, height)
    image = canvasFlatten(image, [0xff, 0xff, 0xff])
    image = canvasThreshold(image, 128)

    const getPixel = (x: number, y: number): number =>
      typeof image.data[(width * y + x) * 4] === 'undefined' ||
      image.data[(width * y + x) * 4] > 128
        ? 0
        : 1

    for (let y = 0; y < height; y++) {
      const lineBytes = Math.ceil(width / 8)
      const bytes = new Uint8Array(lineBytes)

      for (let x = 0; x < width; x += 8) {
        const byteIndex = Math.floor(x / 8)
        let byte = 0

        for (let bit = 0; bit < 8 && x + bit < width; bit++) {
          if (getPixel(x + bit, y)) {
            byte |= 0x80 >> bit
          }
        }
        bytes[byteIndex] = byte
      }

      // b n1 n2 data - StarGraphic image command
      this.queue([0x62]) // 'b'
      this.queue([lineBytes & 0xff, (lineBytes >> 8) & 0xff]) // n1 n2
      this.queue([bytes])
    }

    return this
  }

  /**
   * Cut the paper
   * @param {string} value Cut type ('full' or 'partial')
   * @return {object} Return the object, for easy chaining commands
   */
  cut(_value?: 'full' | 'partial'): this {
    // ESC FF NUL - cut paper
    this.queue([0x1b, 0x0c, 0x00])
    return this
  }

  /**
   * Send a pulse to the cash drawer
   * @return {object} Return the object, for easy chaining commands
   */
  openCashDrawer(): this {
    // BEL - Simple bell command
    this.queue([0x07])
    return this
  }

  /**
   * Encode all previous commands
   * @return {Uint8Array} Return the encoded bytes
   */
  encode(): Uint8Array {
    let length = 0
    this.buffer.forEach(item => {
      if (typeof item === 'number') {
        length++
      } else {
        length += item.length
      }
    })

    const result = new Uint8Array(length)
    let index = 0
    this.buffer.forEach(item => {
      if (typeof item === 'number') {
        result[index] = item
        index++
      } else {
        result.set(item, index)
        index += item.length
      }
    })

    this.reset()
    return result
  }

  /**
   * Add raw printer commands
   * @param {Array} data Raw bytes to be included
   * @return {object} Return the object, for easy chaining commands
   */
  raw(data: (number | Uint8Array)[]): this {
    this.queue(data)
    return this
  }

  /**
   * Encode all previous commands as Base64 string
   * @return {string} Return the encoded bytes as Base64 string
   */
  encodeAsBase64(): string {
    const uint8Array = this.encode()
    let binaryString = ''
    for (let i = 0; i < uint8Array.length; i++) {
      binaryString += String.fromCharCode(uint8Array[i])
    }
    return btoa(binaryString)
  }
}

export default StarGraphicEncoder
